// Planilha Gran — Excel. Dois arquivos:
//   - "para a Gran": o formato do modelo dela (NOME, UC, BOLETO GRAN, EQUATORIAL,
//     COMISSÃO, LINK) mais situação e vencimento, com total que soma TODAS as linhas
//     (a planilha dela somava a comissão só até a linha 23) e uma aba por condomínio.
//   - "conferência": tudo o que a tela mostra, com o motivo de conferência — uso interno.
// Mesma paleta olive e mesmo estilo da exportação da extração de faturas.
import * as XLSXstar from 'xlsx-js-style';
import type { GridModel } from '../../Gestao/Extracao/planilha/faturaGridModel';
import type { LinhaEnvioGran } from './planilhaGranModel';
import { nomeArquivoGran, COLUNAS_SO_NA_TELA } from './planilhaGranModel.ts';

// interop CJS/ESM: sob Vite `XLSXstar.utils` existe; sob Node puro fica em `.default`.
const XLSX: any = (XLSXstar as any).utils ? XLSXstar : (XLSXstar as any).default;

const PAL = { header: '2E3D1F', headerFont: 'FFFFFF', zebra: 'EEF0E6', border: 'D9D9D9', total: 'DDE3C6', revisar: 'FFF2CC' };
const thin = { style: 'thin', color: { rgb: PAL.border } };
const borderAll = { top: thin, bottom: thin, left: thin, right: thin };
const MOEDA = '"R$" #,##0.00';

type Tipo = 'txt' | 'moeda' | 'data' | 'link';
interface Coluna { label: string; tipo: Tipo; largura: number }

/** ISO → serial do Excel, em UTC. Com `Date` local a data ia deslocada pelo fuso
 *  (20/10 saía 19/10 23:59 no teste de 25/09/2026). */
const isoParaSerial = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000);
};

/** Aba com cabeçalho olive, zebra, moeda, link clicável e linha de total opcional. */
function aba(colunas: Coluna[], linhas: (string | number | null)[][], totalDe?: number[], destaque?: number) {
  const ws: any = {};
  const ultima = linhas.length; // linha 0 = cabeçalho
  colunas.forEach((c, ci) => {
    ws[XLSX.utils.encode_cell({ r: 0, c: ci })] = {
      t: 's', v: c.label,
      s: { font: { name: 'Calibri', bold: true, sz: 11, color: { rgb: PAL.headerFont } }, fill: { fgColor: { rgb: PAL.header } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: borderAll },
    };
  });
  linhas.forEach((linha, li) => {
    const r = li + 1;
    const zebra = r % 2 === 0 ? PAL.zebra : 'FFFFFF';
    colunas.forEach((c, ci) => {
      const v = linha[ci];
      const ref = XLSX.utils.encode_cell({ r, c: ci });
      const s: any = { font: { name: 'Calibri', sz: 10, color: { rgb: '212121' } }, fill: { fgColor: { rgb: zebra } }, alignment: { vertical: 'center', horizontal: 'left' }, border: borderAll };
      if (v === null || v === undefined || v === '') { ws[ref] = { t: 's', v: '', s }; return; }
      if (c.tipo === 'moeda' && typeof v === 'number') {
        ws[ref] = { t: 'n', v, z: MOEDA, s: { ...s, alignment: { ...s.alignment, horizontal: 'right' } } };
      } else if (c.tipo === 'data' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        ws[ref] = { t: 'n', v: isoParaSerial(v), z: 'DD/MM/YYYY', s: { ...s, alignment: { ...s.alignment, horizontal: 'center' } } };
      } else if (c.tipo === 'link' && typeof v === 'string' && /^https?:\/\//.test(v)) {
        ws[ref] = { t: 's', v, l: { Target: v }, s: { ...s, font: { ...s.font, color: { rgb: '0563C1' }, underline: true } } };
      } else if (ci === destaque) {
        ws[ref] = { t: 's', v: String(v), s: { ...s, fill: { fgColor: { rgb: PAL.revisar } }, font: { ...s.font, bold: true, color: { rgb: '92400E' } }, alignment: { ...s.alignment, wrapText: true } } };
      } else {
        ws[ref] = { t: typeof v === 'number' ? 'n' : 's', v, s };
      }
    });
  });
  let linhasTotais = 0;
  if (totalDe && linhas.length) {
    const r = ultima + 1;
    linhasTotais = 1;
    const sTotal = { font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: '212121' } }, fill: { fgColor: { rgb: PAL.total } }, border: borderAll, alignment: { vertical: 'center', horizontal: 'right' } };
    colunas.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r, c: ci });
      if (ci === 0) { ws[ref] = { t: 's', v: 'TOTAL', s: { ...sTotal, alignment: { vertical: 'center', horizontal: 'left' } } }; return; }
      if (totalDe.includes(ci)) {
        const col = XLSX.utils.encode_col(ci);
        const soma = linhas.reduce((acc, l) => acc + (typeof l[ci] === 'number' ? (l[ci] as number) : 0), 0);
        // Fórmula de verdade (quem abre no Excel vê a soma) cobrindo da 1ª à última linha.
        ws[ref] = { t: 'n', f: `SUM(${col}2:${col}${ultima + 1})`, v: Math.round(soma * 100) / 100, z: MOEDA, s: sTotal };
        return;
      }
      ws[ref] = { t: 's', v: '', s: sTotal };
    });
  }
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: ultima + linhasTotais, c: colunas.length - 1 } });
  ws['!cols'] = colunas.map(c => ({ wch: c.largura }));
  ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: ultima, c: colunas.length - 1 } }) };
  return ws;
}

const COLUNAS_GRAN: Coluna[] = [
  { label: 'NOME', tipo: 'txt', largura: 44 },
  { label: 'UC', tipo: 'txt', largura: 18 },
  { label: 'BOLETO GRAN', tipo: 'moeda', largura: 15 },
  { label: 'EQUATORIAL', tipo: 'moeda', largura: 15 },
  { label: 'COMISSÃO R$', tipo: 'moeda', largura: 14 },
  { label: 'LINK', tipo: 'link', largura: 60 },
  { label: 'SITUAÇÃO', tipo: 'txt', largura: 38 },
  { label: 'VENCIMENTO', tipo: 'data', largura: 13 },
];

/** Resumo por condomínio a partir das linhas do envio (o mesmo que foi para a Gran). */
function linhasPorCondominio(linhas: LinhaEnvioGran[]) {
  const mapa = new Map<string, { ucs: number; comValor: number; boleto: number; equatorial: number; comissao: number }>();
  for (const l of linhas) {
    const g = mapa.get(l.nome) || { ucs: 0, comValor: 0, boleto: 0, equatorial: 0, comissao: 0 };
    g.ucs++;
    if (l.boleto_gran !== null) g.comValor++;
    g.boleto += l.boleto_gran ?? 0;
    g.equatorial += l.equatorial ?? 0;
    g.comissao += l.comissao ?? 0;
    mapa.set(l.nome, g);
  }
  const r2 = (v: number) => Math.round(v * 100) / 100;
  return Array.from(mapa.entries())
    .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
    // Condomínio sem nenhum valor no mês sai vazio, não "R$ 0,00".
    .map(([nome, g]) => g.comValor === 0
      ? [nome, g.ucs, null, null, null]
      : [nome, g.ucs, r2(g.boleto), r2(g.equatorial), r2(g.comissao)]);
}

/** Excel que vai para a Gran. */
export function baixarPlanilhaParaGran(linhas: LinhaEnvioGran[], mes: string): void {
  XLSX.writeFile(montarPlanilhaParaGran(linhas, mes), nomeArquivoGran(mes));
}

/** O workbook, sem baixar — separado para dar para testar em `node --test`. */
export function montarPlanilhaParaGran(linhas: LinhaEnvioGran[], mes: string): any {
  const wb = XLSX.utils.book_new();
  const rows = linhas.map(l => [l.nome, l.uc, l.boleto_gran, l.equatorial, l.comissao, l.link, l.situacao, l.vencimento]);
  XLSX.utils.book_append_sheet(wb, aba(COLUNAS_GRAN, rows, [2, 3, 4]), mes.replace('/', ''));
  XLSX.utils.book_append_sheet(wb, aba([
    { label: 'NOME', tipo: 'txt', largura: 44 },
    { label: 'UCs', tipo: 'txt', largura: 8 },
    { label: 'BOLETO GRAN', tipo: 'moeda', largura: 15 },
    { label: 'EQUATORIAL', tipo: 'moeda', largura: 15 },
    { label: 'COMISSÃO R$', tipo: 'moeda', largura: 14 },
  ], linhasPorCondominio(linhas), [2, 3, 4]), 'Por condomínio');
  return wb;
}

/** Excel interno de conferência: as duas abas exatamente como na tela. */
export function baixarConferencia(ucs: GridModel, condominios: GridModel, mes: string): void {
  XLSX.writeFile(montarConferencia(ucs, condominios), nomeArquivoGran(mes, true));
}

/** O workbook de conferência, sem baixar — separado para dar para testar. */
export function montarConferencia(ucs: GridModel, condominios: GridModel): any {
  const wb = XLSX.utils.book_new();
  const paraColunas = (g: GridModel, larguras: number[]): Coluna[] => g.columns.map((c, i) => ({
    label: c.label,
    tipo: c.type === 'num' ? 'moeda' : c.type === 'date' ? 'data' : /LINK/i.test(c.label) ? 'link' : 'txt',
    largura: larguras[i] ?? 16,
  }));
  // A grade guarda data como serial do Excel; aqui volta para ISO.
  const EPOCH = new Date(1899, 11, 30).getTime();
  const serialParaIso = (v: unknown) => {
    if (typeof v !== 'number') return v as any;
    const d = new Date(EPOCH + v * 86400000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  // Nº da fatura e origem ficam só na tela (pedido de 25/09/2026).
  const manter = ucs.columns.map((c, i) => (COLUNAS_SO_NA_TELA.includes(c.label) ? -1 : i)).filter(i => i >= 0);
  const larguras = [48, 40, 18, 16, 15, 14, 24, 16, 13, 10, 40, 10, 60, 60];
  const colsUcs = paraColunas({ ...ucs, columns: manter.map(i => ucs.columns[i]) }, manter.map(i => larguras[i]));
  const rowsUcs = ucs.rows.map(r => manter.map((i, j) => (colsUcs[j].tipo === 'data' ? serialParaIso(r[i]) : r[i])));
  const iMoeda = colsUcs.map((c, i) => (c.tipo === 'moeda' ? i : -1)).filter(i => i >= 0);
  XLSX.utils.book_append_sheet(wb, aba(colsUcs, rowsUcs, iMoeda, ucs.highlightCol !== undefined ? manter.indexOf(ucs.highlightCol) : undefined), 'UCs');
  const colsCond = paraColunas(condominios, [44, 8, 12, 10, 10, 11, 11, 16, 16, 14]);
  const iMoedaCond = colsCond.map((c, i) => (c.tipo === 'moeda' ? i : -1)).filter(i => i >= 0);
  XLSX.utils.book_append_sheet(wb, aba(colsCond, condominios.rows, iMoedaCond), 'Condomínios');
  return wb;
}
